import { useEffect, useMemo, useState } from 'react'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type {
  LlmChatMessageFeedbackDto,
  LlmChatFeedbackReason,
  SubmitLlmChatMessageFeedbackPayload,
} from '@/shared/hooks/api/llmChat'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

const FEEDBACK_REASONS: LlmChatFeedbackReason[] = [
  'regulationNotCorrect',
  'linkFailed',
  'notHelpful',
  'questionNotAnswered',
  'other',
]

type LlmChatAssistantMessageFeedbackProps = {
  sessionId: string
  messageId: string
  feedback?: LlmChatMessageFeedbackDto
  isSubmitting: boolean
  onSubmitFeedback: (
    messageId: string,
    payload: SubmitLlmChatMessageFeedbackPayload
  ) => Promise<void>
}

export function LlmChatAssistantMessageFeedback({
  sessionId,
  messageId,
  feedback,
  isSubmitting,
  onSubmitFeedback,
}: LlmChatAssistantMessageFeedbackProps) {
  const { t } = useTranslation('llmChat')
  const feedbackReasonItems = useMemo(
    () =>
      FEEDBACK_REASONS.map((reason) => ({
        value: reason,
        label: t(`feedback.reasons.${reason}`),
      })),
    [t]
  )

  const [isDownvoteFormOpen, setIsDownvoteFormOpen] = useState(false)
  const [selectedReason, setSelectedReason] = useState<
    LlmChatFeedbackReason | undefined
  >(undefined)
  const [otherComment, setOtherComment] = useState('')

  useEffect(() => {
    if (feedback?.rating === 'down') {
      setSelectedReason(feedback.reason)
      setOtherComment(feedback.otherComment ?? '')
      return
    }
    setSelectedReason(undefined)
    setOtherComment('')
  }, [feedback])

  const isPositiveSelected = feedback?.rating === 'up' && !isDownvoteFormOpen
  const isNegativeSelected = feedback?.rating === 'down' || isDownvoteFormOpen

  async function submitUpvote() {
    if (isSubmitting) {
      return
    }
    try {
      await onSubmitFeedback(messageId, { rating: 'up' })
      setIsDownvoteFormOpen(false)
      toast.success(t('feedback.toast.positiveSuccess'))
    } catch {
      toast.error(t('feedback.toast.submitError'))
    }
  }

  function openDownvoteForm() {
    if (isSubmitting) {
      return
    }
    setIsDownvoteFormOpen(true)
  }

  async function submitDownvote() {
    if (isSubmitting || selectedReason === undefined) {
      return
    }
    const payload: SubmitLlmChatMessageFeedbackPayload = {
      rating: 'down',
      reason: selectedReason,
      ...(selectedReason === 'other' &&
        otherComment.trim().length > 0 && {
          otherComment: otherComment.trim(),
        }),
    }
    try {
      await onSubmitFeedback(messageId, payload)
      setIsDownvoteFormOpen(false)
      toast.success(t('feedback.toast.negativeSuccess'))
    } catch {
      toast.error(t('feedback.toast.submitError'))
    }
  }

  return (
    <div className='mt-1 flex flex-col gap-2'>
      <div className='flex items-center justify-end'>
        <p className='text-xs text-muted-foreground'>{t('feedback.prompt')}</p>
        <div className='flex items-center'>
          <Button
            type='button'
            size='icon-xs'
            variant={isPositiveSelected ? 'secondary' : 'ghost'}
            aria-label={t('feedback.thumbsUp')}
            disabled={isSubmitting}
            onClick={submitUpvote}
          >
            <ThumbsUp />
          </Button>
          <Button
            type='button'
            size='icon-xs'
            variant={isNegativeSelected ? 'secondary' : 'ghost'}
            aria-label={t('feedback.thumbsDown')}
            disabled={isSubmitting}
            onClick={openDownvoteForm}
          >
            <ThumbsDown />
          </Button>
        </div>
      </div>

      {isDownvoteFormOpen ? (
        <div className='rounded-md border border-border p-3'>
          <div className='space-y-2'>
            <p className='text-xs text-muted-foreground'>
              {t('feedback.downvoteDialog.title')}
            </p>
            <Select
              value={selectedReason}
              onValueChange={(value) => {
                if (value) {
                  setSelectedReason(value as LlmChatFeedbackReason)
                }
              }}
              items={feedbackReasonItems}
            >
              <SelectTrigger className='w-full'>
                <SelectValue
                  placeholder={t('feedback.downvoteDialog.reasonPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {t(`feedback.reasons.${reason}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedReason === 'other' ? (
              <Textarea
                id={`${sessionId}-${messageId}-feedback-other-comment`}
                value={otherComment}
                onChange={(event) => setOtherComment(event.target.value)}
                placeholder={t(
                  'feedback.downvoteDialog.otherCommentPlaceholder'
                )}
              />
            ) : null}

            <div className='flex items-center gap-2'>
              <Button
                type='button'
                size='sm'
                variant='secondary'
                disabled={isSubmitting || selectedReason === undefined}
                onClick={submitDownvote}
              >
                {t('feedback.downvoteDialog.submit')}
              </Button>
              <Button
                type='button'
                size='sm'
                variant='ghost'
                disabled={isSubmitting}
                onClick={() => setIsDownvoteFormOpen(false)}
              >
                {t('feedback.downvoteDialog.cancel')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
